/**
 * useCloudWatchLinks — composable for building AWS CloudWatch deep-links
 *
 * Used by the Super Admin Toolbar to construct one-click CloudWatch
 * Logs Insights URLs from environment-configured log group names
 * and the current page context.
 *
 * URL format was reverse-engineered from the actual CloudWatch Console
 * and uses the `*XX` hex-encoding that the Console expects.
 */

import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { appConfig } from '@/config'

/** A single toolbar link entry */
export interface CloudWatchLink {
    label: string
    icon: string
    url: string
    /** Tooltip / aria-label */
    description: string
}

/** Context extracted from the current page */
export interface PageContext {
    routeName: string
    entityId: string | null
    organizationId: string | null
}

// ── CloudWatch *XX Encoding ─────────────────────────────────────
// CloudWatch Console uses a non-standard encoding in its hash fragment
// where special characters become *XX (hex). This is NOT percent-encoding.

const CW_ENCODE_MAP: Record<string, string> = {
    ' ': '*20',
    '@': '*40',
    ',': '*2c',
    '\n': '*0a',
    '|': '*7c',
    ':': '*3a',
    '/': '*2f',
    '(': '*28',
    ')': '*29',
    '\'': '*27',
    '"': '*22',
    '#': '*23',
    '$': '*24',
    '%': '*25',
    '&': '*26',
    '+': '*2b',
    ';': '*3b',
    '=': '*3d',
    '?': '*3f',
}

/**
 * Encode a string using CloudWatch's *XX encoding format.
 * Only characters in the map are encoded; the rest pass through.
 */
export function cwEncode(input: string): string {
    let result = ''
    for (const char of input) {
        result += CW_ENCODE_MAP[char] ?? char
    }
    return result
}

/**
 * Generate a simple UUID-v4-like string for the queryId param.
 */
function generateQueryId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

/**
 * Build the full ARN for a CloudWatch log group.
 */
export function buildLogGroupArn(
    region: string,
    accountId: string,
    logGroupName: string,
): string {
    return `arn:aws:logs:${region}:${accountId}:log-group:${logGroupName}`
}

/**
 * Build a CloudWatch Logs Insights URL with a pre-populated query.
 *
 * This matches the exact format the AWS Console generates, including:
 * - Regional subdomain
 * - *XX hex-encoding for query and source
 * - Full ARN-based source
 * - Standard metadata params (lang, logClass, queryBy, tz)
 */
export function buildLogInsightsUrl(
    region: string,
    accountId: string,
    logGroupName: string,
    query: string,
): string {
    return buildMultiSourceInsightsUrl(region, accountId, [logGroupName], query)
}

/**
 * Build a CloudWatch Logs Insights URL that searches across MULTIPLE log groups.
 *
 * This matches the exact format the AWS Console generates, including:
 * - Regional subdomain
 * - *XX hex-encoding for query and source
 * - Full ARN-based sources (one per log group)
 * - Standard metadata params (lang, logClass, queryBy, tz)
 */
export function buildMultiSourceInsightsUrl(
    region: string,
    accountId: string,
    logGroupNames: string[],
    query: string,
): string {
    const base = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}`

    const encodedQuery = cwEncode(query)
    const encodedSources = logGroupNames
        .map(name => `~'${cwEncode(buildLogGroupArn(region, accountId, name))}`)
        .join('')

    const fragment = [
        `#logsV2:logs-insights`,
        `$3FqueryDetail$3D~(`,
        `end~0`,
        `~start~-3600`,
        `~timeType~'RELATIVE`,
        `~tz~'UTC`,
        `~unit~'seconds`,
        `~editorString~'${encodedQuery}`,
        `~queryId~'${generateQueryId()}`,
        `~source~(${encodedSources})`,
        `~lang~'CWLI`,
        `~logClass~'STANDARD`,
        `~queryBy~'logGroupName`,
        `)`,
    ].join('')

    return `${base}${fragment}`
}

/** Build a ready-made Insights query that filters for a specific entity ID */
export function buildEntityQuery(entityId: string): string {
    return `fields @timestamp, @message\n| filter @message like /${entityId}/\n| sort @timestamp desc\n| limit 10000`
}

/** Build a general API logs query (no filter) */
export function buildApiLogsQuery(): string {
    return `fields @timestamp, @message\n| sort @timestamp desc\n| limit 10000`
}

/** Build a ready-made Insights query for worker/job logs */
export function buildWorkerQuery(): string {
    return `fields @timestamp, @message\n| filter @message like /worker|job|queue/\n| sort @timestamp desc\n| limit 10000`
}

/** Build a ready-made Insights query for email/SES logs */
export function buildEmailQuery(): string {
    return `fields @timestamp, @message\n| filter @message like /email|ses|mail/\n| sort @timestamp desc\n| limit 10000`
}

/**
 * Composable: provides reactive CloudWatch links & page context
 * for the Super Admin Toolbar.
 */
export function useCloudWatchLinks() {
    const route = useRoute()
    const authStore = useAuthStore()
    const region = appConfig.awsRegion
    const accountId = appConfig.awsAccountId

    // ── Page context ────────────────────────────────────────────
    const pageContext = computed<PageContext>(() => ({
        routeName: (route.name as string) || route.path,
        entityId: (route.params.id as string) || null,
        organizationId: authStore.currentOrganizationId || null,
    }))

    // ── Configured log groups ───────────────────────────────────
    const logGroups = computed(() => {
        const groups: { key: string; label: string; name: string }[] = []

        if (appConfig.awsLogGroupApi) {
            groups.push({ key: 'api', label: 'API Logs', name: appConfig.awsLogGroupApi })
        }
        if (appConfig.awsLogGroupWorker) {
            groups.push({ key: 'worker', label: 'Worker Logs', name: appConfig.awsLogGroupWorker })
        }
        if (appConfig.awsLogGroupEmail) {
            groups.push({ key: 'email', label: 'Email Logs', name: appConfig.awsLogGroupEmail })
        }

        return groups
    })

    // ── Quick links (Log Insights with default queries) ─────────
    const logGroupLinks = computed<CloudWatchLink[]>(() => {
        if (!accountId) return []

        return logGroups.value.map(g => {
            const query = g.key === 'worker'
                ? buildWorkerQuery()
                : g.key === 'email'
                    ? buildEmailQuery()
                    : buildApiLogsQuery()

            return {
                label: g.label,
                icon: g.key === 'api' ? 'server' : g.key === 'worker' ? 'activity' : 'mail',
                url: buildLogInsightsUrl(region, accountId, g.name, query),
                description: `Open ${g.label} in CloudWatch Logs Insights`,
            }
        })
    })

    // ── Contextual Insights link (only when an entity ID is present) ─
    const insightsLink = computed<CloudWatchLink | null>(() => {
        const entityId = pageContext.value.entityId
        const apiGroup = appConfig.awsLogGroupApi
        if (!entityId || !apiGroup || !accountId) return null

        return {
            label: 'Entity Insights',
            icon: 'search',
            url: buildLogInsightsUrl(region, accountId, apiGroup, buildEntityQuery(entityId)),
            description: `Log Insights filtered to entity ${entityId}`,
        }
    })

    // ── Shortcut Insights links ─────────────────────────────────
    const shortcutLinks = computed<CloudWatchLink[]>(() => {
        if (!accountId) return []

        const links: CloudWatchLink[] = []

        if (appConfig.awsLogGroupWorker) {
            links.push({
                label: 'Worker Insights',
                icon: 'activity',
                url: buildLogInsightsUrl(region, accountId, appConfig.awsLogGroupWorker, buildWorkerQuery()),
                description: 'Log Insights for worker jobs',
            })
        }
        if (appConfig.awsLogGroupEmail) {
            links.push({
                label: 'Email Insights',
                icon: 'mail',
                url: buildLogInsightsUrl(region, accountId, appConfig.awsLogGroupEmail, buildEmailQuery()),
                description: 'Log Insights for email sends',
            })
        }

        return links
    })

    // ── Context chip links (search ALL log groups) ──────────────
    const allLogGroupNames = computed(() => logGroups.value.map(g => g.name))

    /** Link for entity ID chip — searches all log groups for the entity ID */
    const entityIdLink = computed<string | null>(() => {
        const entityId = pageContext.value.entityId
        if (!entityId || !accountId || !allLogGroupNames.value.length) return null
        return buildMultiSourceInsightsUrl(
            region, accountId, allLogGroupNames.value, buildEntityQuery(entityId),
        )
    })

    /** Link for org ID chip — searches all log groups for the organization ID */
    const orgIdLink = computed<string | null>(() => {
        const orgId = pageContext.value.organizationId
        if (!orgId || !accountId || !allLogGroupNames.value.length) return null
        return buildMultiSourceInsightsUrl(
            region, accountId, allLogGroupNames.value, buildEntityQuery(orgId),
        )
    })

    /** True when at least one log group is configured and account ID is set */
    const hasLinks = computed(() => logGroups.value.length > 0 && !!accountId)

    return {
        pageContext,
        logGroupLinks,
        insightsLink,
        shortcutLinks,
        entityIdLink,
        orgIdLink,
        hasLinks,
    }
}
